import { Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { SEO } from "../components/SEO";
import { mockLocations } from "../mock";
import { MapPin, Phone, Mail, Check } from "lucide-react";

export const Locations = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Service Areas | London, Kent & South East England | Hudson Knox"
        description="Professional pergola installation services across London, Kent, Surrey, Sussex, Essex, Hertfordshire and Berkshire. Premium outdoor living throughout South East England."
        keywords="pergola installer London, pergola installer Kent, Surrey, Sussex, Essex, Hertfordshire, Berkshire, South East England, service areas"
        canonicalPath="/locations"
      />
      {/* Hero */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden bg-neutral-900">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-light text-white mb-4">Service Locations</h1>
          <p className="text-xl text-neutral-200">Professional pergola installation across South East England</p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-light text-neutral-900 mb-6">Where We Work</h2>
          <p className="text-lg text-neutral-600 leading-relaxed">
            Hudson Knox Outdoor Living serves residential properties throughout South East England. 
            Our team of expert installers brings luxury outdoor living solutions to homeowners 
            across London, Kent, Surrey, Sussex, Berkshire, Hampshire, and Essex.
          </p>
        </div>
      </section>

      {/* Location Cards */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockLocations.map((location, index) => (
              <Card key={index} className="border-neutral-200 hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center space-x-3 mb-4">
                    <MapPin className="h-6 w-6 text-neutral-900" />
                    <h3 className="text-2xl font-light text-neutral-900">{location.name}</h3>
                  </div>
                  <p className="text-neutral-600 mb-4">
                    Premium outdoor living solutions throughout {location.name}.
                  </p>
                  <div className="space-y-2">
                    {location.areas.slice(0, 6).map((area, idx) => (
                      <div key={idx} className="flex items-center space-x-2 text-sm text-neutral-600">
                        <Check className="h-3 w-3 text-neutral-900 flex-shrink-0" />
                        <span>{area}</span>
                      </div>
                    ))}
                    {location.areas.length > 6 && (
                      <p className="text-sm text-neutral-500 italic pt-2">+ more areas</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Content - London */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative h-96 rounded-lg overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_luxury-pergolas/artifacts/4xysqz7b_image008.png"
                alt="Deponti glass veranda dining installation"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl font-light text-neutral-900 mb-6">Authorised Deponti Stockist - London</h2>
              <p className="text-lg text-neutral-600 mb-4 leading-relaxed">
                As an authorised Deponti stockist, we supply and install premium Dutch aluminium pergola systems across London properties. 
                From the Pinela Deluxe motorised system in Kensington to Giallo Plus glass verandas in Richmond, we deliver professional 
                installation of precision-engineered structures with long-lasting durability.
              </p>
              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                London clients choose us for expert Deponti supply and installation, powder-coated RAL finishes, and premium aluminium 
                systems that add value with minimal maintenance requirements.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-neutral-900 text-white hover:bg-neutral-800">
                  Get London Quote
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content - Kent & Surrey */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-4xl font-light text-neutral-900 mb-6">Authorised Deponti Stockist - Kent & Surrey</h2>
              <p className="text-lg text-neutral-600 mb-4 leading-relaxed">
                Homeowners across Kent and Surrey trust us as their authorised Deponti stockist for premium aluminium pergola systems. 
                From Sevenoaks to Tunbridge Wells, Guildford to Reigate, we supply and install Nebbiolo, Giallo Plus, Pigato and Pinela Deluxe 
                systems - Dutch-engineered structures with precision aluminium components.
              </p>
              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                Our Deponti installations feature powder-coated aluminium frames in RAL colours, safety glass or polycarbonate roofing, 
                and optional motorised louvered systems with smart home integration.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-neutral-900 text-white hover:bg-neutral-800">
                  Get Your Quote
                </Button>
              </Link>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden shadow-2xl order-1 lg:order-2">
              <img
                src="https://customer-assets.emergentagent.com/job_luxury-pergolas/artifacts/z8odfgv9_image003.png"
                alt="Deponti glass sliding walls"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Additional Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-light text-neutral-900 mb-6">Coverage Across South East England</h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              We proudly serve homeowners throughout Sussex, Berkshire, Hampshire, and Essex, bringing 
              the same level of expertise and premium quality to every installation, regardless of location.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              "Brighton & Hove",
              "Winchester",
              "Reading",
              "Chelmsford",
              "Crawley",
              "Basingstoke",
              "Windsor",
              "Colchester",
              "Worthing",
              "Southampton",
              "Maidenhead",
              "Southend-on-Sea"
            ].map((city, index) => (
              <div key={index} className="p-4 border border-neutral-200 rounded-lg hover:shadow-md transition-shadow">
                <p className="font-medium text-neutral-900">{city}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-neutral-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-light mb-6">Serving South East England</h2>
          <p className="text-xl text-neutral-300 mb-8">
            Contact us for a free consultation and quote for your outdoor living project
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <a href="tel:07846923387" className="flex items-center space-x-3 text-lg">
              <Phone className="h-6 w-6" />
              <span>07846 923387</span>
            </a>
            <span className="hidden sm:block text-neutral-600">|</span>
            <a href="mailto:admin@hudsonknox.co.uk" className="flex items-center space-x-3 text-lg">
              <Mail className="h-6 w-6" />
              <span>admin@hudsonknox.co.uk</span>
            </a>
          </div>
          <div className="mt-8">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-neutral-900 hover:bg-neutral-100">
                Request Free Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
